import produce from 'immer';
import {
  BestMatchingProfileListItemResponse,
  CustomerAssignmentFilterListsResponse,
  CustomerDepartmentResponse,
  CustomerInquiryListItemResponse,
  DefaultDeliveryTimeListItemResponse,
  InquiryCreateRequest,
  InquiryDocumentResponse,
  InquiryResponse,
  MetaPagination,
  PspBranchShortInfoResponse,
  QualificationPricingResponse,
  SurchargeListItemResponse,
} from '../../../api';
import { baseInquiryActions } from '../actions';
import { BasePspInquiryOfferInterface } from '../../modules/inquiry/_interfaces/base-psp-inquiry-offer.interface';
import { BaseCustomerInquiryOfferInterface } from '../../modules/inquiry/_interfaces/base-cusomer-inquiry-offer.interface';
import {
  ShiftPlanCalendarDateInterface,
  ShiftPlanCalendarRangeInterface,
} from '../../modules/inquiry/_interfaces/base-shift-plan.interface';
import { InquiryResponseInterface } from '../../modules/inquiry/_interfaces/base-inquiry.interface';
import { BaseDeliveryTimeListItemExtendedInterface } from '../../modules/inquiry/_interfaces/base-delivery-time-list-item-extended.interface';
import { UserShortInfoNameType } from '../../modules/candidates/_types/base-user-full-name.type';

export interface InquiriesState {
  inquiries: {
    data: undefined | any;
    ok: undefined | boolean;
  };
  department: {
    data: CustomerDepartmentResponse | undefined;
    ok: boolean | undefined;
  };
  pspInquiry: {
    data: InquiryResponseInterface | undefined;
    ok: boolean | undefined;
  };
  customerInquiry: {
    data: InquiryResponse | undefined;
    ok: boolean | undefined;
  };
  responsibleUsers: {
    data: UserShortInfoNameType[] | undefined;
    ok: boolean | undefined;
  };
  pspCustomerQualificationPrice: {
    data: QualificationPricingResponse | undefined;
    ok: boolean | undefined;
  };
  pspOfferCreateSurcharges: SurchargeListItemResponse[];
  selectedInquiryFilters: {
    data: undefined | any;
  };
  branches: {
    data: PspBranchShortInfoResponse[] | undefined;
    ok: undefined | boolean;
  };
  inquiryOfferCloseHeaderWrapper: boolean | undefined;
  downloadDocument: {
    data: Blob | undefined;
  };
  inquiryCandidatesList: {
    data: BestMatchingProfileListItemResponse[];
    pagination: MetaPagination | undefined;
    ok: boolean | undefined;
  };
  shiftPlanCalendarData: {
    list: ShiftPlanCalendarDateInterface[];
    formData: (InquiryCreateRequest & { id?: number }) | undefined;
  };
  shiftPlanCalendarRanges: ShiftPlanCalendarRangeInterface[] | undefined;
  pspInquiryOffersList: {
    data: BasePspInquiryOfferInterface[];
    pagination: MetaPagination | undefined;
    ok: boolean | undefined;
  };
  customerInquiryOffersList: {
    data: BaseCustomerInquiryOfferInterface[];
    filters: CustomerAssignmentFilterListsResponse | undefined;
    pagination: MetaPagination | undefined;
    ok: boolean | undefined;
  };
  similarInquiriesList: {
    data: CustomerInquiryListItemResponse[];
    ok: boolean | undefined;
  };
  defaultDeliveryTimeList: {
    data: DefaultDeliveryTimeListItemResponse[];
    ok: boolean | undefined;
  };
  inquiryProvidersList: {
    data: BaseDeliveryTimeListItemExtendedInterface[];
    ok: boolean | undefined;
  };
  inquiryDocumentInfo: {
    data: InquiryDocumentResponse | undefined;
    ok: boolean | undefined;
  };
  downloadedInquiryDocumentFile: {
    data: Blob | undefined;
  };
}

export const INQUIRIES_INIT_STATE: InquiriesState = {
  inquiries: {
    data: undefined,
    ok: undefined,
  },
  department: {
    data: undefined,
    ok: undefined,
  },
  pspInquiry: {
    data: undefined,
    ok: undefined,
  },
  customerInquiry: {
    data: undefined,
    ok: undefined,
  },
  responsibleUsers: {
    data: undefined,
    ok: undefined,
  },
  pspCustomerQualificationPrice: {
    data: undefined,
    ok: undefined,
  },
  pspOfferCreateSurcharges: [],
  selectedInquiryFilters: {
    data: undefined,
  },
  branches: {
    data: undefined,
    ok: undefined,
  },
  inquiryOfferCloseHeaderWrapper: undefined,
  downloadDocument: {
    data: undefined,
  },
  inquiryCandidatesList: {
    data: [],
    pagination: undefined,
    ok: undefined,
  },
  shiftPlanCalendarData: {
    list: [],
    formData: undefined,
  },
  shiftPlanCalendarRanges: undefined,
  pspInquiryOffersList: {
    data: [],
    pagination: undefined,
    ok: undefined,
  },
  customerInquiryOffersList: {
    data: [],
    filters: undefined,
    pagination: undefined,
    ok: undefined,
  },
  similarInquiriesList: {
    data: [],
    ok: undefined,
  },
  defaultDeliveryTimeList: {
    data: [],
    ok: undefined,
  },
  inquiryProvidersList: {
    data: [],
    ok: undefined,
  },
  inquiryDocumentInfo: {
    data: undefined,
    ok: undefined,
  },
  downloadedInquiryDocumentFile: {
    data: undefined,
  },
};

export function BaseInquiryReducer(state: InquiriesState = INQUIRIES_INIT_STATE, action: any): any {
  return produce(state, (draftState) => {
    switch (action.type) {
      case baseInquiryActions.getInquiries.type:
        draftState.inquiries.ok = undefined;
        return;

      case baseInquiryActions.loadInquiries.type:
        draftState.inquiries.data = action.payload;
        return;
      case baseInquiryActions.successLoadInquiries.type:
        draftState.inquiries.ok = true;
        return;

      case baseInquiryActions.errorLoadInquiries.type:
        draftState.inquiries.ok = false;
        return;

      case baseInquiryActions.fetchDepartment.type:
        draftState.department.ok = undefined;
        return;

      case baseInquiryActions.loadDepartment.type:
        draftState.department.data = action.payload;
        return;

      case baseInquiryActions.loadDepartmentSuccess.type:
        draftState.department.ok = true;
        return;

      case baseInquiryActions.loadDepartmentError.type:
        draftState.department.ok = false;
        return;

      case baseInquiryActions.resetDepartment.type:
        draftState.department = INQUIRIES_INIT_STATE.department;
        return;

      case baseInquiryActions.setInquiryShiftViewRangeList.type:
        draftState.shiftPlanCalendarRanges = action.payload;
        return;

      case baseInquiryActions.setShiftPlanInquiryList.type:
        draftState.shiftPlanCalendarData = {
          list: action.payload.list,
          formData: action.payload.formData,
        };
        return;

      case baseInquiryActions.updateShiftPlanInquiryList.type:
        draftState.shiftPlanCalendarData.list = action.payload;
        return;

      case baseInquiryActions.updateShiftPlanFormData.type:
        draftState.shiftPlanCalendarData.formData = action.payload;
        return;

      case baseInquiryActions.resetShiftPlanData.type:
        draftState.shiftPlanCalendarData = INQUIRIES_INIT_STATE.shiftPlanCalendarData;
        draftState.shiftPlanCalendarRanges = INQUIRIES_INIT_STATE.shiftPlanCalendarRanges;
        draftState.similarInquiriesList = INQUIRIES_INIT_STATE.similarInquiriesList;
        return;

      // PSP Inquiry
      case baseInquiryActions.loadPSPInquiry.type:
        draftState.pspInquiry.ok = undefined;
        return;

      case baseInquiryActions.setPSPInquiry.type:
        draftState.pspInquiry.data = action.payload;
        return;

      case baseInquiryActions.successPSPInquiry.type:
        draftState.pspInquiry.ok = true;
        return;

      case baseInquiryActions.errorPSPInquiry.type:
        draftState.pspInquiry.ok = false;
        return;

      case baseInquiryActions.setDownloadInquiryOfferDocumentFile.type:
        draftState.downloadDocument.data = action.payload.document;
        return;

      case baseInquiryActions.clearDownloadInquiryOfferDocumentFile.type:
        draftState.downloadDocument.data = undefined;
        return;

      case baseInquiryActions.loadCustomerInquiry.type:
        draftState.customerInquiry.ok = undefined;
        return;

      case baseInquiryActions.setCustomerInquiry.type:
        draftState.customerInquiry.data = action.payload;
        return;

      case baseInquiryActions.successCustomerInquiry.type:
        draftState.customerInquiry.ok = true;
        return;

      case baseInquiryActions.errorCustomerInquiry.type:
        draftState.customerInquiry.ok = false;
        return;

      case baseInquiryActions.clearInquiryCustomerData.type:
        draftState.customerInquiry.data = undefined;
        draftState.customerInquiry.ok = undefined;
        return;

      // Responsible user list
      case baseInquiryActions.loadResponsibleUserList.type:
        draftState.responsibleUsers.ok = undefined;
        return;

      case baseInquiryActions.setResponsibleUserList.type:
        draftState.responsibleUsers.data = action.payload;
        return;

      case baseInquiryActions.successResponsibleUserList.type:
        draftState.responsibleUsers.ok = true;
        return;

      case baseInquiryActions.errorResponsibleUserList.type:
        draftState.responsibleUsers.ok = false;
        return;

      // PSP Customer Qualification price
      case baseInquiryActions.loadPSPCustomerQualificationPrice.type:
        draftState.pspCustomerQualificationPrice.ok = undefined;
        return;

      case baseInquiryActions.setPSPCustomerQualificationPrice.type:
        draftState.pspCustomerQualificationPrice.data = action.payload;
        return;

      case baseInquiryActions.successPSPCustomerQualificationPrice.type:
        draftState.pspCustomerQualificationPrice.ok = true;
        return;

      case baseInquiryActions.errorPSPCustomerQualificationPrice.type:
        draftState.pspCustomerQualificationPrice.ok = false;
        return;

      case baseInquiryActions.setSurchargesForOfferCreation.type:
        draftState.pspOfferCreateSurcharges = action.payload;
        return;

      case baseInquiryActions.loadInquiryBranches.type:
        draftState.branches.ok = undefined;
        return;

      case baseInquiryActions.setInquiryBranches.type:
        draftState.branches.data = action.payload;
        return;

      case baseInquiryActions.successLoadInquiryBranches.type:
        draftState.branches.ok = true;
        return;

      case baseInquiryActions.errorLoadInquiryBranches.type:
        draftState.branches.ok = false;
        return;

      case baseInquiryActions.loadInquiryCandidatesList.type:
        draftState.inquiryCandidatesList.ok = undefined;
        return;

      case baseInquiryActions.setInquiryCandidatesList.type:
        draftState.inquiryCandidatesList.data = action.payload;
        return;

      case baseInquiryActions.setInitialInquiryCandidatesList.type:
        draftState.inquiryCandidatesList.data = action.payload.data;
        draftState.inquiryCandidatesList.pagination = action.payload.pagination;
        return;

      case baseInquiryActions.addInquiryCandidatesPage.type:
        draftState.inquiryCandidatesList.data = [...draftState.inquiryCandidatesList.data, ...action.payload.data];
        draftState.inquiryCandidatesList.pagination = action.payload.pagination;
        return;

      case baseInquiryActions.resetInquiryCandidatesItems.type:
        draftState.inquiryCandidatesList.data = [];
        draftState.inquiryCandidatesList.pagination = undefined;
        draftState.inquiryCandidatesList.ok = undefined;
        return;

      case baseInquiryActions.setInquiryCandidatesListSuccess.type:
        draftState.inquiryCandidatesList.ok = true;
        return;

      case baseInquiryActions.setInquiryCandidatesListError.type:
        draftState.inquiryCandidatesList.ok = false;
        return;

      case baseInquiryActions.loadSimilarInquiries.type:
        draftState.similarInquiriesList.ok = undefined;
        return;

      case baseInquiryActions.setSimilarInquiries.type:
        draftState.similarInquiriesList.data = action.payload;
        return;

      case baseInquiryActions.successSetSimilarInquiries.type:
        draftState.similarInquiriesList.ok = true;
        return;

      case baseInquiryActions.errorSetSimilarInquiries.type:
        draftState.similarInquiriesList.ok = false;
        return;

      case baseInquiryActions.loadPspInquiryOffersList.type:
        draftState.pspInquiryOffersList.ok = undefined;
        return;

      case baseInquiryActions.setPspInquiryOffersList.type:
        draftState.pspInquiryOffersList.data = action.payload;
        return;

      case baseInquiryActions.addPspInquiryOffersListNextPage.type:
        draftState.pspInquiryOffersList.data = [...draftState.pspInquiryOffersList.data, ...action.payload];
        return;

      case baseInquiryActions.setPspInquiryOffersListPagination.type:
        draftState.pspInquiryOffersList.pagination = action.payload;
        return;

      case baseInquiryActions.setPspInquiryOffersListSuccess.type:
        draftState.pspInquiryOffersList.ok = true;
        return;

      case baseInquiryActions.setPspInquiryOffersListError.type:
        draftState.pspInquiryOffersList.ok = false;
        return;

      case baseInquiryActions.loadCustomerInquiryOffersList.type:
        draftState.customerInquiryOffersList.ok = undefined;
        return;

      case baseInquiryActions.setCustomerInquiryOffersList.type:
        draftState.customerInquiryOffersList.data = action.payload;
        return;

      case baseInquiryActions.addCustomerInquiryOffersListNextPage.type:
        draftState.customerInquiryOffersList.data = [...draftState.customerInquiryOffersList.data, ...action.payload];
        return;

      case baseInquiryActions.setCustomerInquiryOffersFiltersAndPagination.type:
        draftState.customerInquiryOffersList.filters = action.payload.filters;
        draftState.customerInquiryOffersList.pagination = action.payload.pagination;
        return;

      case baseInquiryActions.setCustomerInquiryOffersListSuccess.type:
        draftState.customerInquiryOffersList.ok = true;
        return;

      case baseInquiryActions.setCustomerInquiryOffersListError.type:
        draftState.customerInquiryOffersList.ok = false;
        return;

      case baseInquiryActions.loadDefaultDeliveryTimeList.type:
        draftState.defaultDeliveryTimeList.ok = undefined;
        draftState.defaultDeliveryTimeList.data = [];
        return;

      case baseInquiryActions.setDefaultDeliveryTimeList.type:
        draftState.defaultDeliveryTimeList.data = action.payload;
        return;

      case baseInquiryActions.successDefaultDeliveryTimeList.type:
        draftState.defaultDeliveryTimeList.ok = true;
        return;

      case baseInquiryActions.errorDefaultDeliveryTimeList.type:
        draftState.defaultDeliveryTimeList.ok = false;
        return;

      // Inquiry providers
      case baseInquiryActions.loadInquiryProviders.type:
        draftState.inquiryProvidersList.ok = undefined;
        return;

      case baseInquiryActions.setInquiryProviders.type:
        draftState.inquiryProvidersList.data = action.payload;
        return;

      case baseInquiryActions.successInquiryProviders.type:
        draftState.inquiryProvidersList.ok = true;
        return;

      case baseInquiryActions.errorInquiryProviders.type:
        draftState.inquiryProvidersList.ok = false;
        return;

      case baseInquiryActions.resetCustomerInquiryOffersList.type:
        draftState.customerInquiryOffersList = INQUIRIES_INIT_STATE.customerInquiryOffersList;
        return;

      // Inquiry Document
      case baseInquiryActions.loadInquiryDocument.type:
        draftState.inquiryDocumentInfo.ok = undefined;
        return;

      case baseInquiryActions.setInquiryDocument.type:
        draftState.inquiryDocumentInfo.data = action.payload;
        return;

      case baseInquiryActions.resetInquiryDocument.type:
        draftState.inquiryDocumentInfo = INQUIRIES_INIT_STATE.inquiryDocumentInfo;
        return;

      case baseInquiryActions.successLoadInquiryDocument.type:
        draftState.inquiryDocumentInfo.ok = true;
        return;

      case baseInquiryActions.errorLoadInquiryDocument.type:
        draftState.inquiryDocumentInfo.ok = false;
        return;

      case baseInquiryActions.deleteInquiryDocumentSuccess.type:
        draftState.inquiryDocumentInfo.data = undefined;
        return;

      case baseInquiryActions.setDownloadedInquiryDocumentFile.type:
        draftState.downloadedInquiryDocumentFile.data = action.payload.document;
        return;

      case baseInquiryActions.clearDownloadedInquiryDocumentFile.type:
        draftState.downloadedInquiryDocumentFile.data = undefined;
        return;

      default:
        draftState = { ...draftState };
        return;
    }
  });
}
